(() => {
  const page = document.querySelector('.es-product-page');
  if (!page) return;
  let sequence = 0;
  const directionOf = text => {
    const letters = [...text].filter(char => /\p{Letter}/u.test(char));
    const arabic = letters.filter(char => /\p{Script=Arabic}/u.test(char)).length;
    const latin = letters.filter(char => /\p{Script=Latin}/u.test(char)).length;
    return arabic && arabic >= latin / 2 ? 'rtl' : 'ltr';
  };
  const directText = node => node.textContent.trim();
  const removeOuterHtmlFence = root => {
    // Strip only a paired Markdown wrapper around rendered HTML, never code samples.
    if (!root.querySelector('h1, h2, h3, p, ul, ol, table')) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) if (walker.currentNode.textContent.trim()) nodes.push(walker.currentNode);
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (!first || first === last || first.parentElement.closest('pre, code') || last.parentElement.closest('pre, code')) return;
    const opening = /^\s*```(?:html)?[ \t]*(?:\r?\n|$)/i;
    const closing = /(?:^|\r?\n)[ \t]*```\s*$/;
    if (!opening.test(first.textContent) || !closing.test(last.textContent)) return;
    first.textContent = first.textContent.replace(opening, '');
    last.textContent = last.textContent.replace(closing, '');
    for (const node of [first, last]) {
      const parent = node.parentElement;
      if (parent !== root && parent.matches('p, div, span') && !parent.id && !parent.children.length && !parent.textContent.trim()) parent.remove();
    }
  };
  const prepareCopy = root => {
    if (!root.hasAttribute('dir')) root.setAttribute('dir', 'auto');
    if (root.dataset.direction && root.dataset.direction !== 'auto') return;
    root.querySelectorAll('p, li, h1, h2, h3, h4, th, td, blockquote, caption').forEach(node => {
      if (node.hasAttribute('dir')) return;
      const explicit = node.parentElement.closest('[dir="rtl"], [dir="ltr"]');
      if (explicit && explicit !== root && root.contains(explicit)) return;
      node.dir = directionOf(directText(node));
    });
    root.querySelectorAll('ul, ol, table').forEach(node => {
      if (!node.hasAttribute('dir')) node.dir = directionOf(directText(node));
    });
  };

  class ProductDescription extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.controller?.abort();
        this.controller = new AbortController();
        if (!this.dataset.enhanced) {
          removeOuterHtmlFence(this);
          const requestedDirection = this.dataset.direction || this.getAttribute('dir');
          this.dir = ['rtl', 'ltr'].includes(requestedDirection) ? requestedDirection : directionOf(directText(this));
          if (this.dataset.organize === 'true') this.organize();
        }
        prepareCopy(this);
        if (!this.groups) return;
        const options = {signal: this.controller.signal};
        this.desktop = matchMedia('(min-width: 768px)');
        this.buttons.forEach((button, index) => {
          button.addEventListener('click', () => this.select(index), options);
          button.addEventListener('keydown', event => {
            const rtl = getComputedStyle(this.tabs).direction === 'rtl';
            let next = index;
            if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
            else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = this.buttons.length - 1;
            else return;
            event.preventDefault();
            next = (next + this.buttons.length) % this.buttons.length;
            this.select(next); this.buttons[next].focus();
          }, options);
        });
        const layout = () => {
          this.tabs.hidden = !this.desktop.matches;
          this.groups.forEach((group, index) => {
            const panel = group.querySelector('.es-description-panel');
            if (this.desktop.matches) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', this.buttons[index].id); }
            else { panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); }
          });
          this.select(this.selected || 0);
        };
        this.desktop.addEventListener('change', layout, options);
        layout();
      });
    }
    organize() {
      // Split only recognized authored headings; never infer features or rewrite product text.
      if (this.querySelector('script, style, form, iframe, video')) return;
      const names = /^(specs|specifications?|technical specifications?|how to use|frequently asked questions|faqs?|why choose|المواصفات|مواصفات|طريقة الاستخدام|كيفية الاستخدام|الأسئلة الشائعة|اسئلة شائعة|لماذا تختار|ليه تختار)/i;
      const headings = [...this.querySelectorAll('h2, h3')].filter(node => {
        const nested = node.closest('table, details');
        return names.test(directText(node)) && (!nested || !this.contains(nested));
      });
      if (!headings.length || headings.length > 8) return;
      if (headings.some(node => { let parent = node.parentElement; while (parent && parent !== this) { if (parent.id || parent.hasAttribute('data-shopify-editor-block')) return true; parent = parent.parentElement; } return false; })) return;
      const parts = [];
      const descriptionLabel = (this.dir === 'rtl' ? this.dataset.descriptionLabelAr : this.dataset.descriptionLabelEn) || this.dataset.descriptionLabel || 'Description';
      for (let index = headings.length - 1; index >= 0; index--) {
        const heading = headings[index];
        const range = document.createRange();
        range.setStartBefore(heading); range.setEnd(this, this.childNodes.length);
        parts.unshift({title: directText(heading), content: range.extractContents()});
      }
      const intro = document.createDocumentFragment();
      while (this.firstChild) intro.append(this.firstChild);
      if (intro.textContent.trim() || intro.querySelector('img')) parts.unshift({title: descriptionLabel, content: intro});
      if (parts.length < 2) { parts.forEach(part => this.append(part.content)); return; }
      const id = `es-description-${++sequence}`;
      this.tabs = document.createElement('div'); this.tabs.className = 'es-description-tabs';
      this.tabs.dir = this.dir; this.tabs.setAttribute('role', 'tablist');
      this.tabs.setAttribute('aria-label', descriptionLabel);
      this.groups = []; this.buttons = [];
      parts.forEach((part, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.id = `${id}-tab-${index}`;
        button.textContent = part.title; button.dir = 'auto'; button.setAttribute('role', 'tab'); button.setAttribute('aria-controls', `${id}-panel-${index}`);
        const details = document.createElement('details'); details.className = 'es-description-group';
        const summary = document.createElement('summary'); summary.textContent = part.title; summary.dir = 'auto';
        const panel = document.createElement('div'); panel.id = `${id}-panel-${index}`; panel.className = 'es-description-panel'; panel.tabIndex = 0; panel.append(part.content);
        details.append(summary, panel); this.groups.push(details); this.buttons.push(button); this.tabs.append(button);
      });
      this.append(this.tabs, ...this.groups); this.dataset.enhanced = 'true';
    }
    select(index) {
      this.selected = index;
      this.groups.forEach((group, i) => { group.open = i === index; this.buttons[i].setAttribute('aria-selected', String(i === index)); this.buttons[i].tabIndex = i === index ? 0 : -1; });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('es-product-description')) customElements.define('es-product-description', ProductDescription);

  class MobilePurchase extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.disconnect();
        this.controller = new AbortController();
        const options = {signal: this.controller.signal};
        this.main = document.getElementById(`MainProduct-${this.dataset.section}`);
        this.primary = this.main?.querySelector('.button-trigger__sticky button[name="add"]');
        this.price = this.main?.querySelector('.product-detail__price');
        this.button = this.querySelector('[data-sticky-buy]');
        if (!this.primary || !this.price || !this.button) return;
        this.mobile = matchMedia('(max-width: 767px)');
        const update = () => {
          this.hidden = !(this.mobile.matches && this.primary.getBoundingClientRect().bottom <= 0);
          this.button.disabled = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true' || this.primary.classList.contains('loading');
          this.button.textContent = this.primary.querySelector('.btn-label')?.textContent.trim() || this.primary.textContent.trim();
          this.button.setAttribute('aria-busy', String(this.primary.classList.contains('loading')));
          this.querySelector('[data-sticky-price]').innerHTML = this.price.innerHTML;
        };
        // Observe the whole area before the button, so jumping directly to the top
        // also hides the bar even when the button skips past the viewport.
        this.marker = document.createElement('span');
        this.marker.className = 'es-purchase-observer';
        this.marker.setAttribute('aria-hidden', 'true');
        this.main.append(this.marker);
        const measure = () => {
          this.marker.style.height = `${Math.max(1, this.primary.getBoundingClientRect().bottom - this.main.getBoundingClientRect().top)}px`;
          update();
        };
        this.observer = new IntersectionObserver(update, {threshold: 0}); this.observer.observe(this.marker);
        this.resize = new ResizeObserver(measure); this.resize.observe(this.main);
        this.mutations = new MutationObserver(update);
        this.mutations.observe(this.primary, {attributes: true, childList: true, subtree: true, characterData: true});
        this.mutations.observe(this.price, {childList: true, subtree: true, characterData: true});
        this.mobile.addEventListener('change', update, options);
        this.button.addEventListener('click', () => {
          const form = this.primary.closest('form');
          const invalid = form?.querySelector(':invalid');
          if (invalid) { invalid.scrollIntoView({block: 'center', behavior: 'auto'}); invalid.focus(); return; }
          this.primary.click();
        }, options);
        measure();
      });
    }
    disconnect() { this.controller?.abort(); this.observer?.disconnect(); this.mutations?.disconnect(); this.resize?.disconnect(); this.marker?.remove(); }
    disconnectedCallback() { this.disconnect(); }
  }
  if (!customElements.get('es-mobile-purchase')) customElements.define('es-mobile-purchase', MobilePurchase);

  const markBuyNowPressed = event => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    const button = event.target.closest?.('.es-product-purchase .button-trigger__sticky .shopify-payment-button__button--unbranded');
    const wrapper = button?.closest('.btn-checkout-dynamic');
    if (!wrapper || wrapper.classList.contains('disabled') || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    wrapper.classList.add('es-buy-now-pressed');
    clearTimeout(wrapper.esPressedTimer);
    wrapper.esPressedTimer = setTimeout(() => wrapper.classList.remove('es-buy-now-pressed'), 900);
  };
  page.addEventListener('pointerdown', markBuyNowPressed);
  page.addEventListener('keydown', markBuyNowPressed);

  const prepare = root => {
    root.querySelectorAll('.product-detail__title, .breadcrumb-link, sticky-add-cart .product-item__name, .product_detail-des, .product-detail__short-description, .product-modern-info__panel, .product-modern-feature-bullet__content, .jdgm-rev__body').forEach(node => {
      if (node.querySelector('es-product-description')) return;
      if (!node.hasAttribute('dir') || node.dir === 'auto') node.dir = directionOf(directText(node));
      prepareCopy(node);
    });
  };
  prepare(page);
  document.addEventListener('shopify:section:load', event => { if (page.contains(event.target)) prepare(event.target); });
})();
